import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import CommandTextViewer from '../../../module/task/components/CommandTextViewer';

import autocompleteConfig from '../utils/autocomplete_config';
import {buildRootPatternSource, getPromptSegments, getRecognizedInterpolationRanges} from '../utils/utils';

const SkillsView = (props) => {
  const {onDelete, onEdit, skill} = props;
  const rootPatternSource = buildRootPatternSource(Object.keys(autocompleteConfig));
  const promptValue = skill?.prompt ?? '';
  const promptSegments = React.useMemo(() => {
    const ranges = getRecognizedInterpolationRanges(promptValue, rootPatternSource);
    return getPromptSegments(promptValue, ranges);
  }, [promptValue, rootPatternSource]);

  if (!skill) {
    return (
      <Box
        sx={{
          width: '100%',
          p: '40px',
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#A8A49E',
            lineHeight: '150%',
          }}
        >
          <Translate expression='skills.skills.View.Empty' />
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        p: '40px',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
        overflowY: 'auto',
      }}
    >
      <Stack
        sx={{
          gap: '10px',
        }}
      >
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <Stack
            sx={{
              gap: '12px',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              sx={{
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'Regular',
                  fontSize: '22px',
                  color: '#1A1916',
                  lineHeight: '100%',
                  textDecoration: 'underline',
                  textDecorationColor: '#DA7955',
                  textUnderlineOffset: '5px',
                }}
              >
                /{skill.name}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: '8px',
                  py: '4px',
                  borderRadius: '4px',
                  border: '1px solid #B8D3FF',
                  backgroundColor: '#EFF6FF',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#2B63D9',
                    lineHeight: '100%',
                  }}
                >
                  {skill.outputType}
                </Typography>
              </Box>
            </Stack>
            <Typography
              sx={{
                fontWeight: 400,
                fontStyle: 'Regular',
                fontSize: '14px',
                color: '#A8A49E',
                lineHeight: '100%',
              }}
            >
              {skill.groupName}
            </Typography>
          </Stack>
          <Stack
            direction='row'
            sx={{
              gap: '8px',
            }}
          >
            <Button
              onClick={onEdit}
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#FFFFFF',
                'backgroundColor': '#DA7955',
                '&:hover': {
                  backgroundColor: '#DA7955',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='skills.skills.View.Edit' />
            </Button>
            <Button
              onClick={onDelete}
              variant='outlined'
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#8E8A84',
                'borderColor': '#E8E4DE',
                'backgroundColor': '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E8E4DE',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='skills.skills.View.Delete' />
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            border: '1px solid',
            borderColor: '#E8E4DE',
            borderRadius: '12px',
            px: '14px',
            py: '12px',
            backgroundColor: '#FBFBFB',
          }}
        >
          <Stack
            sx={{
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              <Translate expression='skills.skills.View.Prompt' />
            </Typography>
            <Box
              sx={{
                position: 'relative',
              }}
            >
              {
                promptValue &&
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '150%',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      color: '#1A1916',
                    }}
                  >
                    {
                      promptSegments.map((segment) => (
                        <Box
                          key={segment.key}
                          component='span'
                          sx={{
                            color: segment.highlighted ? '#2B63D9' : 'inherit',
                          }}
                        >
                          {segment.text}
                        </Box>
                      ))
                    }
                  </Box>
                </Box>
              }
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  position: 'relative',
                  zIndex: 2,
                  color: promptValue ? 'transparent' : '#1A1916',
                  WebkitTextFillColor: promptValue ? 'transparent' : 'unset',
                }}
              >
                {promptValue}
              </Typography>
            </Box>
            <Box sx={{borderBottom: '1px solid #E8E4DE'}}></Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#6B6760',
                lineHeight: '150%',
              }}
            >
              <CommandTextViewer
                value={translate('skills.skills.View.InvokeWith', {skillName: skill.name})}
                skill={skill.name}
              />
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

SkillsView.propTypes = {
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  skill: PropTypes.shape({
    groupName: PropTypes.string,
    name: PropTypes.string,
    outputType: PropTypes.string,
    prompt: PropTypes.string,
  }),
};

export default React.memo(SkillsView);
